import { useState } from "react";
import { useHudStore } from "../store/hudStore";
import { DOMAINS, heatColor, stepShare, fmtDate, fmtDay, titleCase, isoToday } from "../data/seed";
import { parseStep, stepStatus, activeSteps, nextCycle } from "../lib/cadence";
import { noteConnections } from "../lib/vaultLinks";
import Page from "../components/Page";
import EditQuestForm from "../components/EditQuestForm";
import { Badge, Button, Card, Field, Progress } from "../design-system/components";
import type { Tone } from "../design-system/components";

// A step can carry Obsidian Tasks fields: 📅 due date, 🔁 recurrence. lib/cadence.ts
// splits them off the label (chips, not raw text) and says which steps are in
// their month; the rest wait, dimmed, showing the date they next come due.
// Full quest detail page (screen: "quest"). Everything about one quest in one
// place: status, dates, the task breakdown, and the lifecycle controls. Edits are
// HUD-local (quests don't yet write back to the vault); the row/board reflect them.
export default function Quest() {
  const st = useHudStore();
  const [stepText, setStepText] = useState("");
  const q = st.quests.find((x) => x.id === st.questId);
  // Name where "back" actually lands. The detail is reachable from the HUD, a
  // main-quest page and a domain page, and a label that says "the HUD" from all
  // three is a small lie the user only discovers by pressing it.
  const backLabel =
    st.questFrom === "main"
      ? "Back to " + (st.mains.find((m) => m.id === st.main)?.name ?? "the main quest")
      : st.questFrom === "domain"
        ? "Back to " + st.domain
        : "Back to the HUD";

  if (!q) {
    return (
      <Page back={backLabel} onBack={st.closeQuest}>
        <Card className="detail-stack">
          <h1 className="detail-heading detail-title">That quest is gone.</h1>
          <p className="detail-copy">Return to your quests to choose the next action.</p>
          <div className="detail-actions"><Button onClick={st.closeQuest}>{backLabel}</Button></div>
        </Card>
      </Page>
    );
  }

  const done = !!st.done[q.id];
  const skipped = !!st.skipped[q.id];
  const steps = q.steps || [];
  const today = isoToday();
  const month = today.slice(0, 7);
  // Only the steps in their month take part (lib/cadence.ts); the rest wait, dimmed.
  const active = activeSteps(steps, month);
  const stepsDone = active.filter((i) => done || st.stepDone[q.id + ":" + i]).length;
  const pct = Math.round((stepsDone / Math.max(1, active.length)) * 100);
  const nextOn = active.length === 0 ? nextCycle(steps, month) : "";
  const d = DOMAINS.find((x) => x.name === q.domain);
  const main = st.mains.find((m) => m.id === q.main);
  const domColor = heatColor(d ? d.health : 60);
  const editing = st.editingId === q.id;

  // The schema's status and the shared badges carry the same state meaning.
  const status: { label: string; tone: Tone } = done
    ? { label: "Done", tone: "success" }
    : skipped
      ? { label: "Skipped", tone: "warning" }
      : stepsDone > 0
        ? { label: "Doing", tone: "accent" }
        : { label: "To do", tone: "neutral" };

  const meta: { label: string; value: string; accent?: string }[] = [
    { label: "Due", value: fmtDate(q.due), accent: q.due ? "var(--orange-ink)" : undefined },
    { label: "Created", value: fmtDate(q.created) },
    { label: "Updated", value: fmtDate(q.updated) },
    { label: "Completed", value: fmtDate(q.completed), accent: q.completed ? "var(--green-ink)" : undefined },
  ];

  const addStep = () => { const t = stepText.trim(); if (t) { st.addQuestStep(q.id, t); setStepText(""); } };

  // Vault connections: the quest's own note plus the notes it links out to / that
  // link back to it, resolved against the loaded vault (see lib/vaultLinks).
  const notePath = q.notePath;
  const notes = st.vaultNotes;
  const noteLoaded = !!(notePath && notes && notes[notePath]);
  const { outgoing, backlinks } = noteConnections(notePath, notes);
  const connected = Array.from(new Set([...outgoing, ...backlinks]));
  const baseName = (p: string) => (p.split("/").pop() || p).replace(/\.md$/i, "");

  return (
    <Page back={backLabel} onBack={st.closeQuest}>
      <Card className="quest-detail-hero detail-stack">
        <div className="detail-actions">
          <Badge tone={status.tone}>{status.label}</Badge>
          {q.pinned && <Badge tone="accent"><i aria-hidden="true" className="fa-solid fa-thumbtack" />Pinned for today</Badge>}
          <span className="detail-xp quest-detail-xp" style={{ color: done ? "var(--mut)" : "var(--accent-ink)" }}>+{q.xp} XP</span>
        </div>
        <h1 className="detail-heading detail-title" style={{ color: done ? "var(--mut)" : "var(--ink)", textDecoration: done ? "line-through" : "none" }}>{q.title}</h1>
        <div className="detail-actions">
          <Button size="compact" onClick={() => st.openMain(q.main || "m1")} icon={main?.fa || "fa-solid fa-circle-question"} style={{ color: main?.color || "var(--sub)" }}>{main?.name || "Unassigned"}</Button>
          <Button size="compact" onClick={() => st.openDomain(q.domain)} icon={<b className="detail-status-dot" style={{ background: domColor }} />}>{q.domain}</Button>
          <Badge>{titleCase(q.type)}</Badge>
        </div>
      </Card>

      <Card className="quest-dates">
        <div className="quest-dates-grid">
          {meta.map((m) => m.label === "Due" ? (
            <div key={m.label} className="quest-date-item quest-date-control">
              <Field id="quest-due-date" className="quest-date-input" label="Due" type="date" value={q.due || ""} onChange={(event) => st.setQuestDue(q.id, event.target.value)} style={{ color: q.due ? "var(--orange-ink)" : "var(--mut)" }} />
            </div>
          ) : (
            <div key={m.label} className="quest-date-item">
              <span className="quest-date-label">{m.label}</span>
              <span className="quest-date-value" style={{ color: m.accent || "var(--sub)" }}>{m.value}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="detail-stack" title="Task breakdown" actions={active.length > 0 ? <Badge tone="accent">{stepsDone} / {active.length} complete</Badge> : undefined}>
        {active.length > 0 && <Progress value={pct} label="Current cycle" valueLabel={`${stepsDone} of ${active.length} tasks complete`} />}
        {steps.length > 0 && active.length === 0 && <p className="detail-copy">Nothing due this month · next {fmtDate(nextOn)}</p>}
        <div className="quest-step-list">
          {steps.map((text, i) => {
            const { label, recur } = parseStep(text);
            // The date is this month's occurrence, or the next cycle for a waiting step.
            const { active: inMonth, on: due } = stepStatus(text, month);
            const sd = inMonth && (done || !!st.stepDone[q.id + ":" + i]);
            const overdue = inMonth && !!due && !sd && due < today;
            const isToday = inMonth && !!due && !sd && due === today;
            const dueTone: Tone = sd || !inMonth ? "neutral" : overdue ? "danger" : isToday ? "warning" : "neutral";
            const locked = done || !inMonth;
            return (
              <div key={i} className="quest-step" title={inMonth ? undefined : "Not due this month. Next on " + fmtDate(due)} style={{ opacity: inMonth ? 1 : 0.55 }}>
                <Button variant="quiet" size="compact" className="quest-step-toggle" onClick={() => st.toggleStep(q.id, i)} aria-label={`${sd ? "Mark incomplete" : "Complete"}: ${label}`} aria-pressed={sd} disabled={locked}>
                  <span className="quest-step-check" style={{ borderRadius: sd ? 6 : 999, borderColor: sd ? "transparent" : "var(--control-edge)", background: sd ? "var(--accent)" : "transparent" }}>
                    <i className="fa-solid fa-check" aria-hidden="true" style={{ opacity: sd ? 1 : 0 }} />
                  </span>
                </Button>
                <span className="quest-step-title" style={{ color: sd ? "var(--mut)" : "var(--sub)", textDecoration: sd ? "line-through" : "none" }}>{label}</span>
                <span className="quest-step-meta">
                  {recur && <Badge tone="accent" className="quest-step-recurrence" title={`Repeats ${recur}`}><i aria-hidden="true" className="fa-solid fa-repeat" />{recur}</Badge>}
                  {due && <Badge tone={dueTone}><i aria-hidden="true" className="fa-solid fa-calendar-day" />{inMonth ? "" : "next "}{fmtDay(due)}</Badge>}
                  {inMonth && <span className="detail-xp" style={{ color: sd ? "var(--mut)" : "var(--accent-ink)" }}>+{stepShare(q, active, i)}</span>}
                </span>
                <Button variant="quiet" size="compact" className="quest-step-remove" onClick={() => st.removeQuestStep(q.id, i)} aria-label={`Remove step: ${label}`} icon="fa-solid fa-xmark" />
              </div>
            );
          })}
          {steps.length === 0 && <p className="detail-copy">No steps yet. Break this quest into smaller tasks below.</p>}
        </div>
        <form className="quest-step-add" onSubmit={(event) => { event.preventDefault(); addStep(); }}>
          <Field id="quest-add-task" label="Add a task" value={stepText} onChange={(event) => setStepText(event.target.value)} placeholder="Draft the outline" />
          <Button type="submit" icon="fa-solid fa-plus">Add</Button>
        </form>
      </Card>

      <Card className="detail-stack" title="In the vault" actions={connected.length > 0 ? <Badge tone="accent">{connected.length} linked</Badge> : undefined}>
        {notePath ? (
          <>
            <Button className="quest-note-link" onClick={() => st.openVaultNote(notePath)} icon="fa-solid fa-file-lines">
              <span className="quest-note-copy"><strong>{q.title}</strong><span>{notePath}</span></span>
              <i aria-hidden="true" className="fa-solid fa-arrow-up-right-from-square" />
            </Button>
            {connected.length > 0 ? (
              <div className="quest-note-connections">
                <h3 className="detail-subheading">Connected notes</h3>
                {connected.map((p) => (
                  <Button key={p} variant="quiet" className="quest-note-link" onClick={() => st.openVaultNote(p)} icon={backlinks.includes(p) ? "fa-solid fa-arrow-left-long" : "fa-solid fa-arrow-right-long"} title={backlinks.includes(p) ? "Links to this quest" : "This quest links out to it"}>
                    <span className="quest-note-copy"><strong>{baseName(p)}</strong></span>
                    {notes?.[p]?.front?.type && <Badge>{notes[p]?.front.type}</Badge>}
                  </Button>
                ))}
              </div>
            ) : noteLoaded ? <p className="detail-copy">No connections yet. Add <b>[[wikilinks]]</b> in the note to weave it into the graph.</p> : null}
          </>
        ) : <p className="detail-copy">This quest isn't linked to a vault note yet.</p>}
      </Card>

      <Card className="quest-detail-actions">
        <Button variant={done ? "secondary" : "primary"} onClick={() => done ? st.undo(q.id) : st.complete(q.id)} icon={done ? "fa-solid fa-rotate-left" : "fa-solid fa-check"}>{done ? "Mark not done" : "Complete quest"}</Button>
        <Button variant={q.pinned ? "primary" : "secondary"} onClick={() => st.pin(q.id)} aria-pressed={q.pinned} icon="fa-solid fa-thumbtack">{q.pinned ? "Unpin" : "Pin for today"}</Button>
        <Button onClick={() => st.focus(q.id)} icon="fa-solid fa-stopwatch">Focus</Button>
        <Button className="quest-detail-edit" variant={editing ? "primary" : "secondary"} onClick={() => st.startEdit(q.id)} aria-pressed={editing} icon="fa-solid fa-pen">Edit</Button>
        <Button variant="danger" onClick={() => st.removeQuest(q.id)} icon="fa-solid fa-trash">Delete</Button>
      </Card>
      {editing && <EditQuestForm quest={q} />}
    </Page>
  );
}
